import { Frustum, Matrix, Plane, ShaderMaterial, type AbstractMesh, type Camera, type Scene } from "@babylonjs/core";

// A small world-space margin retains tangent surfaces and floating-point boundary cases.
const CULL_MARGIN = 0.001;

/** A plain, reused list: never mutate Babylon's observed RTT renderList. */
export class CaptureRenderList {
  private readonly meshes: AbstractMesh[] = [];
  private readonly planes = Frustum.GetPlanes(Matrix.Identity());

  constructor(
    private readonly scene: Scene,
    private readonly include: (mesh: AbstractMesh) => boolean,
    private readonly sky: AbstractMesh | null = null,
  ) {}

  /** Called after capture observers establish the actual (possibly mirrored) view and clip plane. */
  select(camera: Camera | null = this.scene.activeCamera, clipPlane: Plane | null | undefined = this.scene.clipPlane): AbstractMesh[] {
    Frustum.GetPlanesToRef(this.scene.getTransformMatrix(), this.planes);
    let count = 0;
    const sceneMeshes = this.scene.meshes;
    for (let i = 0; i < sceneMeshes.length; i++) {
      const mesh = sceneMeshes[i]!;
      if (mesh.isDisposed() || !mesh.isEnabled() || !mesh.isVisible || mesh.isBlocked
        || (camera && (mesh.layerMask & camera.layerMask) === 0) || !this.include(mesh)) continue;
      // Vector/parent motion can occur within one render id (including capture
      // observers). Babylon's normal same-id fast path would retain stale bounds.
      // Only force unsynchronized transforms, including their moving ancestors.
      mesh.computeWorldMatrix(!mesh.isSynchronized());
      // Camera-relative sky shaders do not consume Babylon's clip plane. Deformed,
      // instanced and billboard geometry may extend beyond its cached base bounds;
      // retain it rather than introducing a new source of missing capture pixels.
      if (mesh === this.sky || mesh.infiniteDistance || mesh.alwaysSelectAsActiveMesh || mesh.billboardMode !== 0
        || mesh.skeleton || mesh.morphTargetManager || mesh.bakedVertexAnimationManager?.isEnabled
        || mesh.hasThinInstances || mesh.isAnInstance || mesh.material instanceof ShaderMaterial
        || ("instances" in mesh && (mesh.instances as AbstractMesh[]).length > 0)
        || ("getLODLevels" in mesh && (mesh as AbstractMesh & { getLODLevels(): unknown[] }).getLODLevels().length > 0)) {
        this.meshes[count++] = mesh;
        continue;
      }
      const bounds = mesh.getBoundingInfo();
      if (!bounds || bounds.isLocked) {
        this.meshes[count++] = mesh;
        continue;
      }
      const { minimumWorld: min, maximumWorld: max } = bounds.boundingBox;
      if (!Number.isFinite(min.x + min.y + min.z + max.x + max.y + max.z)) {
        this.meshes[count++] = mesh;
        continue;
      }
      let outside = false;
      for (let p = 0; p < this.planes.length; p++) {
        const plane = this.planes[p]!;
        const n = plane.normal;
        // Maximum plane distance of the world AABB: reject only when the whole
        // bound is outside. This also keeps near/far and reflected-view edges.
        const distance = n.x * (n.x >= 0 ? max.x : min.x)
          + n.y * (n.y >= 0 ? max.y : min.y) + n.z * (n.z >= 0 ? max.z : min.z) + plane.d;
        if (distance < -CULL_MARGIN) {
          outside = true;
          break;
        }
      }
      if (outside) continue;
      if (clipPlane) {
        const n = clipPlane.normal;
        // Babylon retains the negative half-space; intersecting bounds survive.
        const distance = n.x * (n.x >= 0 ? min.x : max.x)
          + n.y * (n.y >= 0 ? min.y : max.y) + n.z * (n.z >= 0 ? min.z : max.z) + clipPlane.d;
        if (distance > CULL_MARGIN) continue;
      }
      this.meshes[count++] = mesh;
    }
    this.meshes.length = count;
    return this.meshes;
  }

  clear(): void {
    this.meshes.length = 0;
  }
}
